import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// ── Accent colour helper ──────────────────────────────────
export function storyAccent(story) {
  if (story.is_manual) return '#C98900'
  if (story.is_rubric) return '#5E80FB'
  return '#B33F1F'
}

// ── Droppable column ──────────────────────────────────────
export function DroppableColumn({ id, children, className, style }) {
  const { setNodeRef, isOver } = useDroppable({ id, data: { type: 'column', columnId: id } })
  return (
    <div ref={setNodeRef} className={className + (isOver ? ' drag-over' : '')} style={style}>
      {children}
    </div>
  )
}

// ── Sortable card wrapper ─────────────────────────────────
export function SortableCard({ story, inQueue, onDelete, onClick }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: story.id,
    animateLayoutChanges: () => false
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : 'transform 250ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    opacity: isDragging ? 0 : 1,
    zIndex: isDragging ? 999 : undefined,
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <StoryCard story={story} inQueue={inQueue} onDelete={onDelete} onClick={onClick} />
    </div>
  )
}

export function DragOverlayCard({ story }) {
  return (
    <div style={{ transform: 'rotate(3deg)', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', borderRadius: 3 }}>
      <StoryCard story={story} inQueue={false} onDelete={() => {}} onClick={() => {}} />
    </div>
  )
}

// ── Story Card ────────────────────────────────────────────
export function StoryCard({ story, inQueue, onDelete, onClick }) {
  const hasText = !!(story.edited_text || story.draft_text)
  const hasImage = !!story.image_url
  const isNew = story.status === 'selected' || story.status === 'proposed'
  const accent = storyAccent(story)

  return (
    <div className={'story-card-k' + (isNew ? ' story-card-k--new' : '')} style={{ borderLeftColor: accent }}>
      <div className="card-k-header">
        <span className="card-k-slug" style={{ color: isNew ? '#c8c0b8' : accent }}>{story.sport_slug}</span>
        <button className="card-k-delete-wrap" onClick={e => { e.stopPropagation(); onDelete() }}>
          <span className="card-k-delete-icon">✕</span>
        </button>
      </div>
      <p className="card-k-headline" onClick={onClick}>{story.headline}</p>
      <div className="card-k-indicators">
        <span className="card-k-indicator" style={{ color: hasText ? accent : '#c8c0b8' }}>✎</span>
        <span className="card-k-indicator card-k-indicator--lg" style={{ color: hasImage ? accent : '#c8c0b8' }}>⊞</span>
      </div>
    </div>
  )
}

// ── Intro Card ────────────────────────────────────────────
export function IntroCard({ intro, onRefresh, onClick }) {
  const hasContent = !!(intro?.text || intro?.subject)
  return (
    <div className="intro-card" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="intro-card-header">
        <span className="intro-card-label">INTRO</span>
        <button className="intro-refresh-btn"
          onClick={e => { e.stopPropagation(); onRefresh() }}
          disabled={intro?.loading} title="Regenerate">
          {intro?.loading ? '…' : '↺'}
        </button>
      </div>
      {intro?.loading ? (
        <div className="intro-loading">
          <div className="loading-spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />
        </div>
      ) : hasContent ? (
        <div className="intro-clip">
          <p className="intro-text">
            {intro?.subject && <span className="intro-subject-preview">{intro.subject}</span>}
            {intro?.text}
          </p>
          <div className="intro-fade" />
        </div>
      ) : (
        <p className="intro-placeholder-text">Click to edit</p>
      )}
    </div>
  )
}
